'use client';

import { useState, useEffect } from 'react';
import { getSupabase } from '@/lib/supabase';
import { useCalendarStore } from '@/stores/calendar-store';
import { uiFb } from '@/lib/i18n';
import type { User } from '@supabase/supabase-js';

const DEDUP_STORAGE_KEY = 'almanak_rated_pages';

async function getAnonUserId(): Promise<string> {
  try {
    const res = await fetch('https://api.ipify.org?format=text');
    const ip = await res.text();
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(ip));
    return 'anon-' + Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return 'anon-unknown';
  }
}

function getRatedPages(): Set<string> {
  try {
    const raw = localStorage.getItem(DEDUP_STORAGE_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function markPageRated(slug: string): void {
  try {
    const rated = getRatedPages();
    rated.add(slug);
    localStorage.setItem(DEDUP_STORAGE_KEY, JSON.stringify([...rated]));
  } catch { /* localStorage unavailable */ }
}

interface FeedbackItem {
  id: string;
  user_name: string | null;
  user_avatar: string | null;
  rating: number;
  comment: string | null;
  created_at: string;
}

/** Compute average rating from items, rounded to 1 decimal. Returns null if empty. */
export function computeAvgRating(items: { rating: number }[]): string | null {
  if (items.length === 0) return null;
  return (items.reduce((sum, i) => sum + i.rating, 0) / items.length).toFixed(1);
}

/** Render star characters for a numeric rating (0-5) */
export function renderStarString(value: number): string {
  return Array.from({ length: 5 }, (_, i) => (i < value ? '\u2605' : '\u2606')).join('');
}

export function FeedbackTab({ pageSlug, user }: { pageSlug: string; user: User | null }) {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [alreadyRated, setAlreadyRated] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const lang = useCalendarStore((s) => s.lang);

  useEffect(() => {
    const sb = getSupabase();
    if (!sb) return;
    sb.from('feedback')
      .select('id, user_name, user_avatar, rating, comment, created_at')
      .eq('page_slug', pageSlug)
      .order('created_at', { ascending: false })
      .limit(50)
      .then(({ data }) => {
        if (data) setItems(data);
      });
  }, [pageSlug]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setAlreadyRated(getRatedPages().has(pageSlug));
    }
  }, [pageSlug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const sb = getSupabase();
    if (rating === 0 || !sb) return;

    // Honeypot: silently fake success
    if (honeypot) {
      setAlreadyRated(true);
      return;
    }

    setSubmitting(true);
    const { data, error } = await sb
      .from('feedback')
      .insert({
        page_slug: pageSlug,
        user_id: user?.id ?? await getAnonUserId(),
        user_name: user ? (user.user_metadata?.full_name ?? user.email ?? 'Anonymous') : 'Anonymous',
        user_avatar: user?.user_metadata?.avatar_url ?? null,
        rating,
        comment: user && comment.trim() ? comment.trim() : null,
      })
      .select()
      .single();

    if (!error && data) {
      setItems((prev) => [data, ...prev]);
      window._paq?.push(['trackEvent', 'Feedback', 'Submit', pageSlug, rating]);
      markPageRated(pageSlug);
      setAlreadyRated(true);
      setRating(0);
      setComment('');
    }
    setSubmitting(false);
  };

  const avgRating = computeAvgRating(items);

  return (
    <div className="flex flex-col gap-4">
      {/* Summary */}
      {avgRating && (
        <div className="flex items-center gap-2 text-sm text-muted">
          <span className="font-medium text-text-primary">{avgRating}</span>
          <Stars value={Math.round(Number(avgRating))} lang={lang} />
          <span>({items.length} {items.length === 1 ? uiFb('ui.review', 'review', lang) : uiFb('ui.reviews', 'reviews', lang)})</span>
        </div>
      )}

      {/* Submit form */}
      {alreadyRated ? (
        <p className="text-sm text-muted">{uiFb('ui.already_rated', 'You already rated this page. Thank you!', lang)}</p>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex items-center gap-1">
            <span className="mr-2 text-sm text-text-secondary">{uiFb('ui.your_rating', 'Your rating:', lang)}</span>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="text-lg transition-transform hover:scale-110"
                aria-label={`${star} star${star > 1 ? 's' : ''}`}
              >
                {star <= (hoverRating || rating) ? '\u2605' : '\u2606'}
              </button>
            ))}
          </div>
          {user ? (
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={uiFb('ui.leave_comment', 'Leave a comment (optional)', lang)}
              rows={2}
              className="content-card px-3 py-2 text-sm text-text-primary placeholder:text-muted focus:border-brand focus:outline-none"
            />
          ) : (
            <p className="text-xs text-muted">{uiFb('ui.sign_in_to_comment', 'Sign in to leave a comment', lang)}</p>
          )}

          {/* Honeypot — hidden from real users */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
            <label htmlFor="feedback-website">Website</label>
            <input
              id="feedback-website"
              type="text"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <button
            type="submit"
            disabled={rating === 0 || submitting}
            className="self-end rounded-lg bg-brand px-4 py-1.5 text-sm font-medium text-text-on-brand transition-opacity disabled:opacity-50"
          >
            {submitting ? uiFb('ui.sending', 'Sending...', lang) : uiFb('ui.submit', 'Submit', lang)}
          </button>
        </form>
      )}

      {/* List */}
      <div className="flex flex-col gap-3">
        {items.map((item) => (
          <div key={item.id} className="content-card p-3">
            <div className="flex items-center gap-2">
              {item.user_avatar && (
                <img src={item.user_avatar} alt="" className="h-5 w-5 rounded-full" />
              )}
              <span className="text-xs font-medium text-text-secondary">{item.user_name ?? uiFb('ui.anonymous', 'Anonymous', lang)}</span>
              <Stars value={item.rating} lang={lang} />
              <span className="ml-auto text-label text-muted">
                {new Date(item.created_at).toLocaleDateString()}
              </span>
            </div>
            {item.comment && (
              <p className="mt-1.5 text-sm text-text-secondary">{item.comment}</p>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <p className="py-4 text-center text-sm text-muted">{uiFb('ui.no_feedback_yet', 'No feedback yet. Be the first!', lang)}</p>
        )}
      </div>
    </div>
  );
}

function Stars({ value, lang }: { value: number; lang: string }) {
  return (
    <span className="text-sm text-semantic-warning" aria-label={`${value} ${uiFb('ui.out_of_5_stars', 'out of 5 stars', lang)}`}>
      {renderStarString(value)}
    </span>
  );
}
